import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { Search, Plus, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassPill from '@/components/ui/GlassPill';
import { useAgentChatResearchGenerationWithTracking } from '@/hooks/ai-agents/functions/core/useAgentChatResearchGenerationWithTracking';
import { useRecommendationCardState, RECOMMENDATION_CONFIGS } from '@/hooks/ai-agents/functions/core/createRecommendationTrackingWrapper';
import { useRecommendationTracking } from '@/hooks/ai-agents/functions/core/useRecommendationTracking';
import { useViewRecommendationNavigation } from '@/hooks/ai-agents/functions/core/useViewRecommendationNavigation';
import { useThreadContext } from '@/hooks/ai-agents/functions/core/useThreadContext';
import { useNavigate } from 'react-router-dom';
import { 
  getProjectInfoByGoalId,
  getProjectNavigationUrl,
  getGoalNavigationUrl,
  useNavigationWithModalClose
} from '@/hooks/ai-agents/functions/core/useResponseParsing';
import { cn } from '@/lib/utils';
import { ExtractedResearch } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { 
  getCardContainerClasses, 
  getCardIconClasses, 
  getCardButtonClasses 
} from './shared/RecommendationCardStyles';

interface ChatRecommendedResearchCardProps {
  research: ExtractedResearch;
  threadId: string;
  message: MessageDisplay; // Changed from messageId: string to message: MessageDisplay
  type?: 'research';
  className?: string;
}

type SaveState = 'idle' | 'saving' | 'success' | 'saved' | 'error';

/**
 * Chat Recommended Research Card - Individual research item with save button
 * Shows research recommendations with option to generate and save to goal research board
 * Now with standardized recommendation tracking
 */
export const ChatRecommendedResearchCard: React.FC<ChatRecommendedResearchCardProps> = ({
  research,
  threadId,
  message, // Changed from messageId to message
  type = 'research',
  className
}) => {
  const { navigateAndCloseModal } = useNavigationWithModalClose();
  
  // Get thread context to find the goal ID
  const { data: threadContext } = useThreadContext(threadId);
  
  // Use standardized tracking state management with full message object
  const { localState, setLocalState } = useRecommendationCardState(message, 'research');
  
  // Research generation with tracking using full message object
  const { mutateWithTracking, isGenerating } = useAgentChatResearchGenerationWithTracking(message);
  
  // Get saved recommendation data for view functionality using full message object
  const { isRecommendationSaved } = useRecommendationTracking(message.id, message.databaseId);
  
  // View navigation functionality
  const { viewResearch } = useViewRecommendationNavigation();
  
  // Handle viewing the saved research
  const handleViewResearch = async () => {
    const status = await isRecommendationSaved('research');
    if (status.isSaved && status.savedItemId && status.metadata) {
      const savedRecommendation = {
        id: status.savedItemId,
        type: 'research' as const,
        savedAt: status.savedAt!,
        metadata: status.metadata
      };
      viewResearch(savedRecommendation);
    } else {
      console.error('[ChatRecommendedResearchCard] No saved research data found');
    }
  };
  
  // Get config for this recommendation type
  const config = RECOMMENDATION_CONFIGS.research;
  
  // Research now always renders and goes to private research library (no project dependencies)

  // Handle saving this specific research
  const handleSaveResearch = async () => {
    console.log('[ChatRecommendedResearchCard] 🚀 STARTING handleSaveResearch - Private Research');
    
    console.log('[ChatRecommendedResearchCard] 🔄 Setting state to saving...');
    setLocalState('saving');
    
    const researchParams = {
      research, // Pass the full research object as expected by the hook
      messageId: message.id // Use message.id for consistency
      // No goalId - private research
    };
    
    console.log('[ChatRecommendedResearchCard] 🔍 Research parameters:', researchParams);
    console.log('[ChatRecommendedResearchCard] 🔍 mutateWithTracking function:', typeof mutateWithTracking);
    
    try {
      console.log('[ChatRecommendedResearchCard] 🔄 Calling mutateWithTracking...');
      const result = await mutateWithTracking(researchParams);
      console.log('[ChatRecommendedResearchCard] ✅ mutateWithTracking completed:', result);
      
      console.log('[ChatRecommendedResearchCard] 🔄 Setting state to saved...');
      setLocalState('saved');
      
    } catch (error) {
      console.error('[ChatRecommendedResearchCard] ❌ Failed to save research:', error);
      setLocalState('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setLocalState('idle');
      }, 3000);
    }
  };

  // Get button content based on state
  const getButtonContent = () => {
    switch (localState) {
      case 'saving':
        return <Loader2 size={12} className="animate-spin" />;
      case 'success':
        return <Check size={12} />;
      case 'saved':
        return <Search size={12} />; // Changed from Library to Search
      case 'error':
        return <AlertCircle size={12} />;
      default:
        return <Plus size={12} />;
    }
  };

  // Get button text based on state
  const getButtonText = () => {
    switch (localState) {
      case 'saving':
        return config.savingText;
      case 'success':
        return config.successText;
      case 'saved':
        return config.savedText;
      case 'error':
        return config.errorText;
      default:
        return config.saveText;
    }
  };

  // Get button click handler based on state
  const getButtonHandler = () => {
    switch (localState) {
      case 'saved':
        return handleViewResearch;
      default:
        return handleSaveResearch;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        variants={CHAT_ANIMATIONS.cardEntry}
        initial="initial"
        animate="animate"
        exit="exit"
        className={cn("mt-3 w-full", className)}
      >
        <div className={cn(getCardContainerClasses(type))}>
          <div className="flex items-start gap-2.5">
            {/* Save Button - Left Side */}
            <div className="flex-shrink-0 pt-0.5">
              <Button
                size="sm"
                variant="outline"
                onClick={getButtonHandler()}
                disabled={localState === 'saving' || localState === 'success'}
                className={cn(
                  "h-7 px-3 text-xs border-emerald-300 dark:border-emerald-600",
                  "hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
                  "transition-all duration-200",
                  // State-specific styling
                  localState === 'success' && 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400',
                  localState === 'saved' && 'border-gray-300 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200',
                  localState === 'error' && 'border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {getButtonContent()}
                  <span>{getButtonText()}</span>
                </span>
              </Button>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <span className="text-sm text-gray-700 dark:text-gray-200 block font-medium">
                {research.title}
              </span>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                {research.description}
              </p>
              
              {/* Private Research - No project navigation needed */}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}; 